import { Injectable } from '@angular/core';

const ACCESS_TOKEN_KEY = 'auth.accessToken';
const REFRESH_TOKEN_KEY = 'auth.refreshToken';

@Injectable({
  providedIn: 'root',
})
export class TokenStorageService {
  private accessTokenMemory: string | null = null;
  private refreshTokenMemory: string | null = null;
  /**
   * Set by `clear()`: memory being null then means "signed out", not "not
   * loaded yet", so a token the browser refused to delete is never read back
   * (F-008). `save()` of a real session lifts it.
   */
  private cleared = false;

  save(accessToken: string | null, refreshToken: string | null): void {
    this.accessTokenMemory = accessToken ?? null;
    this.refreshTokenMemory = refreshToken ?? null;
    this.cleared = accessToken == null && refreshToken == null;

    this.persist(ACCESS_TOKEN_KEY, accessToken);
    this.persist(REFRESH_TOKEN_KEY, refreshToken);
  }

  getAccessToken(): string | null {
    if (this.accessTokenMemory || this.cleared) {
      return this.accessTokenMemory;
    }
    const token = this.read(ACCESS_TOKEN_KEY);
    this.accessTokenMemory = token;
    return token;
  }

  getRefreshToken(): string | null {
    if (this.refreshTokenMemory || this.cleared) {
      return this.refreshTokenMemory;
    }
    const token = this.read(REFRESH_TOKEN_KEY);
    this.refreshTokenMemory = token;
    return token;
  }

  clear(): void {
    this.accessTokenMemory = null;
    this.refreshTokenMemory = null;
    this.cleared = true;
    this.remove(ACCESS_TOKEN_KEY);
    this.remove(REFRESH_TOKEN_KEY);
  }

  private persist(key: string, value: string | null): void {
    if (value == null) {
      this.remove(key);
      return;
    }

    let storedInSession = false;
    try {
      sessionStorage.setItem(key, value);
      storedInSession = true;
    } catch {
      storedInSession = false;
    }

    if (storedInSession) {
      this.removeFromLocalStorage(key);
    }
  }

  private read(key: string): string | null {
    let value: string | null = null;
    try {
      value = sessionStorage.getItem(key);
    } catch {
      value = null;
    }
    if (value) {
      return value;
    }

    value = this.readFromLocalStorage(key);
    if (value) {
      this.migrateLocalToSession(key, value);
    }

    return value;
  }

  private readFromLocalStorage(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private remove(key: string): void {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // ignore
    }
    this.removeFromLocalStorage(key);
  }

  private removeFromLocalStorage(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }

  private migrateLocalToSession(key: string, value: string): void {
    let stored = false;
    try {
      sessionStorage.setItem(key, value);
      stored = true;
    } catch {
      stored = false;
    }
    if (stored) {
      this.removeFromLocalStorage(key);
    }
  }
}
